import { useState, useRef, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Sparkles,
  Send,
  Loader2,
  User,
  Bot,
  X,
  Trash2,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const API_URL = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL || '';

export const AIAssistantDrawer = ({ open, onOpenChange }) => {
  const { session, organization } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [quota, setQuota] = useState(null);
  const messagesEndRef = useRef(null);
  
  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  // Check quota on open
  useEffect(() => {
    if (open && organization?.id && session?.access_token) {
      checkQuota();
    }
  }, [open, organization?.id, session?.access_token]);
  
  const checkQuota = async () => {
    try {
      const res = await fetch(`${API_URL}/api/ai/quota/check`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ organization_id: organization.id }),
      });
      
      if (res.ok) {
        const data = await res.json();
        setQuota(data);
      }
    } catch (error) {
      console.error('Quota check error:', error);
    }
  };
  
  const sendMessage = async () => {
    if (!input.trim() || loading || !organization?.id) return;
    
    const userMessage = { role: 'user', content: input.trim() };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    
    try {
      const res = await fetch(`${API_URL}/api/ai/assistant/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          organization_id: organization.id,
          message: userMessage.content,
          conversation_history: messages.slice(-6),
        }),
      });
      
      const data = await res.json();
      
      if (data.success) {
        setMessages((prev) => [...prev, { role: 'assistant', content: data.response }]);
        // Update quota
        if (data.quota) {
          setQuota(data.quota);
        }
      } else {
        if (data.feature_disabled) {
          toast.error('AI Assistant is not enabled for your organization');
        } else if (data.error?.includes('quota')) {
          toast.error('Monthly AI quota exceeded');
        } else {
          toast.error(data.error || 'Failed to get response');
        }
        // Remove the user message if failed
        setMessages((prev) => prev.slice(0, -1));
      }
    } catch (error) {
      console.error('Chat error:', error);
      toast.error('Failed to send message');
      setMessages((prev) => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };
  
  const clearChat = () => {
    setMessages([]);
  };
  
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };
  
  const suggestedQuestions = [
    "Who are my least engaged members?",
    "What's our dues collection rate?",
    "How did our last event perform?",
    "What should I focus on this week?",
  ];
  
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[400px] sm:w-[450px] bg-deep-space border-l border-white/[0.08] p-0 flex flex-col">
        <SheetHeader className="p-4 border-b border-white/[0.08]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-electric-ice-blue flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <SheetTitle className="text-cloud-white">AI Assistant</SheetTitle>
                <p className="text-xs text-muted-text">Chapter Intelligence</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {messages.length > 0 && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={clearChat}
                  className="text-muted-text hover:text-signal-red"
                  data-testid="clear-chat-btn"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
          {quota && (
            <div className="mt-2 flex items-center justify-between text-xs">
              <span className="text-muted-text">
                {quota.remaining} / {quota.limit} calls remaining
              </span>
              <span className={cn(
                "px-2 py-0.5 rounded-full",
                quota.plan_tier === 'free' ? 'bg-soft-amber/20 text-soft-amber' : 'bg-fresh-mint/20 text-fresh-mint'
              )}>
                {quota.plan_tier || 'free'}
              </span>
            </div>
          )}
        </SheetHeader>
        
        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 ? (
            <div className="space-y-4">
              <div className="text-center py-8">
                <Sparkles className="w-12 h-12 mx-auto mb-4 text-electric-ice-blue/50" />
                <p className="text-soft-silver text-sm">
                  Ask me anything about your chapter data
                </p>
              </div>
              
              <div className="space-y-2">
                <p className="text-xs text-muted-text uppercase tracking-wider">Suggested questions</p>
                {suggestedQuestions.map((question, idx) => (
                  <button
                    key={idx}
                    onClick={() => setInput(question)}
                    className="w-full text-left px-3 py-2 text-sm text-soft-silver bg-obsidian/50 rounded-lg border border-white/[0.04] hover:bg-white/[0.04] hover:border-electric-ice-blue/30 transition-colors"
                    data-testid={`suggested-q-${idx}`}
                  >
                    {question}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((msg, idx) => (
              <div
                key={idx}
                className={cn(
                  "flex gap-3",
                  msg.role === 'user' ? 'justify-end' : 'justify-start'
                )}
              >
                {msg.role === 'assistant' && (
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-electric-ice-blue flex items-center justify-center flex-shrink-0">
                    <Bot className="w-4 h-4 text-white" />
                  </div>
                )}
                <div
                  className={cn(
                    "max-w-[80%] px-4 py-3 rounded-2xl text-sm",
                    msg.role === 'user'
                      ? 'bg-electric-ice-blue text-obsidian rounded-br-sm'
                      : 'bg-obsidian/70 text-cloud-white rounded-bl-sm border border-white/[0.08]'
                  )}
                >
                  <p className="whitespace-pre-wrap">{msg.content}</p>
                </div>
                {msg.role === 'user' && (
                  <div className="w-8 h-8 rounded-lg bg-soft-silver/20 flex items-center justify-center flex-shrink-0">
                    <User className="w-4 h-4 text-soft-silver" />
                  </div>
                )}
              </div>
            ))
          )}
          
          {loading && (
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-electric-ice-blue flex items-center justify-center">
                <Bot className="w-4 h-4 text-white" />
              </div>
              <div className="px-4 py-3 rounded-2xl rounded-bl-sm bg-obsidian/70 border border-white/[0.08]">
                <Loader2 className="w-4 h-4 animate-spin text-electric-ice-blue" />
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
        
        {/* Input Area */}
        <div className="p-4 border-t border-white/[0.08]">
          <div className="flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask about your chapter..."
              disabled={loading || (quota && !quota.allowed)}
              className="flex-1 bg-obsidian border-white/[0.08] text-cloud-white placeholder:text-muted-text"
              data-testid="ai-chat-input"
            />
            <Button
              onClick={sendMessage}
              disabled={!input.trim() || loading || (quota && !quota.allowed)}
              className="bg-electric-ice-blue text-obsidian hover:bg-electric-ice-blue/90"
              data-testid="ai-send-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
          </div>
          {quota && !quota.allowed && (
            <p className="text-xs text-signal-red mt-2">
              Monthly AI quota exceeded. Upgrade your plan for more calls.
            </p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default AIAssistantDrawer;
